/**
 * The release system as one machine, for the main release flow. Pure and
 * React-free so it is unit-tested.
 *
 * The flow used to talk about the two VMs directly: the operator learned which
 * machine each phase runs on, noticed which was off, and pressed Start on the
 * right card at the right moment. That is an implementation detail of a tool whose
 * job is to guide someone through a release, and it was the single most confusing
 * thing about it. So the main flow has one subject — "the release system" — which
 * is asleep or awake, and one pair of actions: wake it, and let it sleep. The two
 * host pages keep the per-machine view, because troubleshooting genuinely is about
 * a particular machine.
 *
 * Waking starts *both* VMs even though only one of them is doing anything, and that
 * is the trade this module exists to make: the alternative is the operator knowing
 * which half of the release they are in. Being blocked is still per phase
 * (`REQUIRED_HOSTS`), so the prepare host reaching its idle stop during a long
 * deploy does not send the page back to the sleeping panel.
 */
import { HOST_ROLES, REQUIRED_HOSTS, type HostRole, type Phase } from '@finvari/deployer';
import { bootOverdue, formatDuration, type HostView } from './hostStatus';

/**
 * What the flow says about the system.
 *
 *  - `loading` — no snapshot has arrived for a host the phase needs. Distinct from
 *    `asleep` for the same reason `HostView` separates them: offering Wake for a
 *    system that is already up is a click nobody needed to make.
 *  - `awake` — every host this phase needs is answering, so the flow's own content
 *    is what the page should show.
 *  - `waking` — one of them is booting. Nothing to press; the panel counts.
 *  - `sleeping` — one of them is on its way down, which is a state the operator
 *    just asked for. Waking it would race the halt, so the panel waits it out.
 *  - `asleep` — stopped, never reported, or a boot that never produced a heartbeat.
 */
export type SystemState = 'loading' | 'awake' | 'waking' | 'sleeping' | 'asleep';

/** One host's contribution: its presentation, and how long its boot should take. */
export interface SystemHost {
    view: HostView;
    /** From the host registry; undefined while the VM is unprovisioned. */
    expectedBootSeconds?: number;
}

export type SystemHosts = Record<HostRole, SystemHost>;

export interface SystemView {
    state: SystemState;
    /** The hosts this phase's work cannot proceed without. */
    required: HostRole[];
    /**
     * The hosts a wake would start: every VM that is not already up or on its way,
     * not just the required one. A host mid-halt is excluded — `instances.start`
     * against a shutdown already under way either fails or brings back a VM that is
     * still powering off, which is why the host card withholds power control there.
     */
    wakeTargets: HostRole[];
    /** The hosts answering right now — what a sleep would stop. */
    online: HostRole[];
    /**
     * When the system starts going to sleep on its own: the earliest of the online
     * hosts' idle deadlines. The earliest rather than the latest because it is the
     * first thing that changes under the operator, and one timer is the whole point
     * of this view — a flow that showed two would be back to naming the machines.
     * Undefined when every online host's timer is suspended (held, or work running).
     */
    sleepsInMs?: number;
    /** Some online host is holding its idle timer open. */
    held: boolean;
    /** A host the phase needs has been booting for longer than it should. */
    overdue: boolean;
}

/**
 * Whether a wake would do anything to this host — the same rule the host card
 * applies to its power control, so the two never disagree about whether Start is
 * the right thing to offer. A boot that has overrun counts: it is the retry.
 */
function isWakeable(host: SystemHost): boolean {
    const { presentation } = host.view;
    if (presentation === 'offline' || presentation === 'unknown') return true;
    return (
        presentation === 'starting' &&
        host.expectedBootSeconds !== undefined &&
        bootOverdue(host.view, host.expectedBootSeconds)
    );
}

/**
 * The system as the release flow sees it, from the phase and the two host
 * documents' presentations.
 */
export function systemView(phase: Phase, hosts: SystemHosts): SystemView {
    const required = REQUIRED_HOSTS[phase];
    const requiredHosts = required.map((role) => hosts[role]);
    const online = HOST_ROLES.filter((role) => hosts[role].view.presentation === 'online');
    const wakeTargets = HOST_ROLES.filter((role) => isWakeable(hosts[role]));
    const overdue = requiredHosts.some(
        (h) => h.expectedBootSeconds !== undefined && bootOverdue(h.view, h.expectedBootSeconds),
    );

    // Only the online hosts' timers: a stopped host has no deadline, and a starting
    // one has not armed its first yet.
    const deadlines = online
        .map((role) => hosts[role].view.idleStopInMs)
        .filter((ms): ms is number => ms !== undefined);

    return {
        state: stateOf(requiredHosts),
        required,
        wakeTargets,
        online,
        sleepsInMs: deadlines.length > 0 ? Math.min(...deadlines) : undefined,
        held: online.some((role) => hosts[role].view.held),
        overdue,
    };
}

/**
 * The state, decided over the hosts the phase needs. `loading` first, because it
 * is the absence of an answer rather than an answer; then the good case; then the
 * two transitions, which are the ones with nothing for the operator to do.
 */
function stateOf(requiredHosts: SystemHost[]): SystemState {
    if (requiredHosts.some((h) => h.view.presentation === 'loading')) return 'loading';
    if (requiredHosts.every((h) => h.view.presentation === 'online')) return 'awake';
    if (requiredHosts.some((h) => h.view.presentation === 'starting')) return 'waking';
    if (requiredHosts.some((h) => h.view.presentation === 'stopping')) return 'sleeping';
    return 'asleep';
}

/** Whether the flow should show its own content rather than the sleeping panel. */
export function isAwake(view: SystemView): boolean {
    return view.state === 'awake';
}

/**
 * How long the system has, in one sentence.
 *
 * The answers are genuinely different and the operator acts on the difference: a
 * countdown is a deadline, a hold is a decision someone made, and a suspended timer
 * means the work itself is keeping the system up.
 *
 * A hold and a countdown can both be true, because each is read across the online
 * hosts: `held` is any of them holding, `sleepsInMs` the earliest deadline among
 * the rest. A host suspends its deadline while it is held (`IdleController`), so
 * the two facts together mean one VM is staying up by decision while another is
 * still counting down — and "held awake" alone would hide a stop that is coming.
 */
export function sleepText(system: SystemView): string {
    if (system.sleepsInMs === undefined) {
        return system.held
            ? 'Awake, and held awake until the hold is cleared.'
            : 'Awake. The sleep timer is paused while something is running.';
    }
    const countdown = formatDuration(system.sleepsInMs);
    if (system.held) {
        return `Awake. Part of the system is held awake, and the rest sleeps in ${countdown} unless something runs.`;
    }
    return `Awake, and sleeps in ${countdown} unless something runs.`;
}
